import React, { useMemo } from 'react';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { WorkItem } from '../api/workItems';
import {
    formatClassificationInfluencesSummary,
    getWorkItemCreatedFromLabel,
    getWorkItemSourceInfo,
} from '../utils/workItemDisplay';
import {
    bestGuessWhy,
    isBestGuessPlacement,
} from '../utils/classificationBestGuess';
import {
    isSpecificDomain,
    isSuspiciousWorkItemDisplay,
    normalizeClassificationSource,
    normalizeDomainKey,
} from '../utils/classificationSuspicion';
import type { SourceDocumentState } from './WorkItemDrawerHeader';

export type TrustStripState = 'ready' | 'neutral' | 'review' | 'source-unavailable';
type SourceUnavailableReason = 'missing' | 'deleted' | 'unavailable' | 'unknown-error';

export interface TrustStripViewModel {
    state: TrustStripState;
    title: string;
    summary: string;
    tone: 'success' | 'neutral' | 'warning' | 'info';
    isCritical: boolean;
    domainKey: string;
    domainLabel: string;
    sourceLabel: string;
    classificationLabel: string;
    confidenceLabel: string;
    evidenceLabel: string;
    influenceLines: string[];
    influenceOverflow: number;
    bestGuess: boolean;
    bestGuessWhy: string;
}

const CLASSIFICATION_LABELS: Record<string, string> = {
    heuristic: 'Heuristics',
    ai_enrichment: 'AI enrichment',
    user_override: 'Your correction',
    bulk_user_approved: 'Bulk approval',
    migration: 'Migration',
    system_default: 'System default',
    unknown: 'Unknown',
};

function humanizeDomain(domain: string): string {
    const normalized = normalizeDomainKey(domain || 'general') || 'general';
    if (normalized === 'job_hunt') return 'Job Search';
    return normalized
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatConfidence(item: WorkItem): string {
    if (typeof item.confidence_numeric === 'number' && Number.isFinite(item.confidence_numeric)) {
        const bounded = Math.min(1, Math.max(0, item.confidence_numeric));
        return `${Math.round(bounded * 100)}%`;
    }
    return 'Not recorded';
}

function documentUnavailableReason(
    item: WorkItem,
    sourceDocumentState?: SourceDocumentState
): SourceUnavailableReason | null {
    const source = getWorkItemSourceInfo(item);
    if (source.sourceType !== 'document') return null;
    if (sourceDocumentState === 'missing') return 'missing';
    if (sourceDocumentState === 'deleted') return 'deleted';
    if (sourceDocumentState === 'unknown_error') return 'unknown-error';

    const status = String(item.meta?.source_document_status || '').trim().toLowerCase();
    if (status === 'missing' || status === 'deleted' || status === 'unavailable') {
        return status;
    }
    return null;
}

export function isManualClassificationNeutral(item: WorkItem): boolean {
    return getWorkItemSourceInfo(item).sourceType === 'manual'
        && normalizeClassificationSource(item.classification_source) === 'unknown';
}

export function deriveTrustStripViewModel(item: WorkItem, sourceDocumentState?: SourceDocumentState): TrustStripViewModel {
    const domainKey = normalizeDomainKey(item.domain || 'general') || 'general';
    const domainLabel = humanizeDomain(domainKey);
    const sourceInfo = getWorkItemSourceInfo(item);
    const sourceLabel = getWorkItemCreatedFromLabel(item);
    const classificationSource = normalizeClassificationSource(item.classification_source);
    const classificationLabel = CLASSIFICATION_LABELS[classificationSource] || 'Unknown';
    const evidence = typeof item.evidence_summary === 'string' ? item.evidence_summary.trim() : '';
    const influences = formatClassificationInfluencesSummary(item.source_lineage);
    const suspicious = isSuspiciousWorkItemDisplay(item);
    const sourceUnavailable = documentUnavailableReason(item, sourceDocumentState);
    const harmlessUnknown =
        classificationSource === 'unknown' &&
        (!isSpecificDomain(domainKey) || sourceInfo.sourceType === 'manual' || domainKey === 'general');

    const base = {
        domainKey,
        domainLabel,
        sourceLabel,
        classificationLabel,
        confidenceLabel: formatConfidence(item),
        evidenceLabel: evidence || 'No evidence summary retained.',
        influenceLines: influences.lines,
        influenceOverflow: Math.max(0, influences.total - influences.lines.length),
        bestGuess: isBestGuessPlacement(item),
        bestGuessWhy: bestGuessWhy(item),
    };

    if (harmlessUnknown) {
        return {
            ...base,
            state: 'neutral',
            title: sourceInfo.sourceType === 'manual' ? 'Added by you' : 'No classification needed yet',
            summary: `${sourceLabel} · ${domainLabel}`,
            tone: 'neutral',
            isCritical: false,
        };
    }

    if (base.bestGuess) {
        return {
            ...base,
            state: 'review',
            title: `Best guess: ${domainLabel}`,
            summary: base.bestGuessWhy,
            tone: 'neutral',
            isCritical: false,
        };
    }

    if (suspicious && isSpecificDomain(domainKey)) {
        return {
            ...base,
            state: 'review',
            title: `Confirm ${domainLabel} placement`,
            summary: 'Signals are weak or missing for this specific domain.',
            tone: 'warning',
            isCritical: true,
        };
    }

    if (sourceUnavailable) {
        return {
            ...base,
            state: 'source-unavailable',
            title: sourceUnavailable === 'unknown-error' ? 'Source status unavailable' : 'Source unavailable',
            summary: sourceUnavailable === 'unknown-error'
                ? 'Unable to verify the linked source right now.'
                : 'The linked source document may no longer be available.',
            tone: 'info',
            isCritical: false,
        };
    }

    return {
        ...base,
        state: 'ready',
        title: 'Ready to act',
        summary: `${sourceLabel} · ${classificationLabel}`,
        tone: 'success',
        isCritical: false,
    };
}

export interface LifeAreaOption {
    key: string;
    label: string;
    disabled?: boolean;
}

interface WorkItemTrustStripProps {
    item: WorkItem;
    expanded: boolean;
    detailsId: string;
    sourceDocumentState?: SourceDocumentState;
    domainActionsDisabled?: boolean;
    lifeAreaOptions?: LifeAreaOption[];
    onConfirmDomain: () => void;
    onMoveToGeneral: () => void;
    onCorrectLifeArea?: (domainKey: string) => void;
    children: React.ReactNode;
}

export const trustPanelChipSx = {
    height: 24,
    fontWeight: 600,
    bgcolor: 'var(--color-badge-bg)',
    color: 'var(--color-badge-text)',
    border: '1px solid var(--color-border-hover)',
    '& .MuiChip-label': { px: 1 },
} as const;

/** Optional classification and provenance; source recovery lives in the header. */
export const WorkItemTrustStrip: React.FC<WorkItemTrustStripProps> = ({
    item, expanded, detailsId, sourceDocumentState, domainActionsDisabled = false,
    lifeAreaOptions = [], onConfirmDomain, onMoveToGeneral, onCorrectLifeArea, children,
}) => {
    const model = useMemo(() => deriveTrustStripViewModel(item, sourceDocumentState), [item, sourceDocumentState]);
    const selectOptions = useMemo(() => {
        if (!model.domainKey || lifeAreaOptions.some((option) => option.key === model.domainKey)) {
            return lifeAreaOptions;
        }
        return [
            { key: model.domainKey, label: model.domainLabel, disabled: true },
            ...lifeAreaOptions,
        ];
    }, [lifeAreaOptions, model.domainKey, model.domainLabel]);
    return (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box component="section" data-testid="WB_TRUST_STRIP" sx={{ px: 2, py: 1 }}>
                <Box id={detailsId} data-testid="WB_TRUST_DETAILS_PANEL" role="region"
                    aria-label="Trust details" tabIndex={-1}
                    sx={{ border: '1px solid var(--color-border)', borderRadius: 1, bgcolor: 'var(--color-bg-secondary)' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, p: 1.25, alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faCircleInfo} aria-hidden />
                        <Chip size="small" label={model.sourceLabel} sx={trustPanelChipSx} />
                        <Chip size="small" label={model.classificationLabel} sx={trustPanelChipSx} />
                        {!model.bestGuess && (
                            <Chip size="small" label={model.confidenceLabel} sx={trustPanelChipSx} />
                        )}
                    </Box>
                    {model.bestGuess && (
                        <Box data-testid="WB_TRUST_BEST_GUESS" sx={{ px: 1.25, pb: 1.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                Best guess: {lifeAreaOptions.find((option) => option.key === model.domainKey)?.label || model.domainLabel}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', mt: 0.5 }}>
                                {model.bestGuessWhy}
                            </Typography>
                            {onCorrectLifeArea && lifeAreaOptions.length > 0 && (
                                <Select
                                    data-testid="WB_TRUST_LIFE_AREA"
                                    SelectDisplayProps={{ 'aria-label': 'Life area' }}
                                    value={selectOptions.some((option) => option.key === model.domainKey) ? model.domainKey : selectOptions[0].key}
                                    disabled={domainActionsDisabled}
                                    onChange={(event) => {
                                        const next = String(event.target.value || '');
                                        if (!next || next === model.domainKey) return;
                                        onCorrectLifeArea(next);
                                    }}
                                    variant="filled"
                                    sx={{
                                        mt: 1,
                                        minHeight: 44,
                                        color: 'var(--color-text-primary)',
                                        bgcolor: 'var(--color-bg-secondary)',
                                        '& .MuiSvgIcon-root': { color: 'var(--color-text-primary)' },
                                    }}
                                >
                                    {selectOptions.map((option) => (
                                        <MenuItem key={option.key} value={option.key} disabled={Boolean(option.disabled)}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        </Box>
                    )}
                    {model.isCritical && !model.bestGuess && (
                        <Box data-testid="WB_TRUST_ACTION_WARNING" sx={{ px: 1.25, pb: 1 }}>
                            <Typography variant="body2">Is this in the right area?</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Button data-testid="WB_DRAWER_CONFIRM_DOMAIN" disabled={domainActionsDisabled}
                                    onClick={onConfirmDomain} sx={{ textTransform: 'none', minHeight: 44 }}>
                                    Confirm {model.domainLabel}
                                </Button>
                                <Button data-testid="WB_DRAWER_MOVE_GENERAL" disabled={domainActionsDisabled}
                                    onClick={onMoveToGeneral} sx={{ textTransform: 'none', minHeight: 44 }}>
                                    Move to General
                                </Button>
                            </Box>
                        </Box>
                    )}
                    <Divider />
                    {children}
                </Box>
            </Box>
        </Collapse>
    );
};

export default WorkItemTrustStrip;
